import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { DESIGN_REQUESTS } from "@/constants/designRequestTestIds";
import ApprovalBanner from "@/components/ApprovalBanner";
import WorkflowStepper from "@/components/WorkflowStepper";
import InputsStep from "@/components/InputsStep";
import DesignStep from "@/components/DesignStep";
import PreviewPromptModal from "@/components/PreviewPromptModal";

const STATUS_COPY = {
  draft:      { label: "Draft",       color: "#6B6358" },
  generating: { label: "Generating…", color: "#B5462F" },
  ready:      { label: "Ready",       color: "#1F6B6B" },
  failed:     { label: "Failed",      color: "#B5462F" },
};

export default function DesignRequestDetail() {
  const { brandId, designRequestId } = useParams();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [document, setDocumentVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stepOverride, setStepOverride] = useState(null);
  const [furthestStep, setFurthestStep] = useState("inputs");
  const [showPromptModal, setShowPromptModal] = useState(false);
  const pollRef = useRef(null);

  const STEPS_ORDER = ["inputs", "content", "design"];

  // Bump furthest whenever the marketer advances past it.
  useEffect(() => {
    if (!stepOverride) return;
    if (STEPS_ORDER.indexOf(stepOverride) > STEPS_ORDER.indexOf(furthestStep)) {
      setFurthestStep(stepOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepOverride]);

  const load = async () => {
    const { data } = await api.get(`/brands/${brandId}/design-requests/${designRequestId}`);
    setRequest(data);
    if (data.document_version_id) {
      try {
        const r = await api.get(`/document-versions/${data.document_version_id}`);
        setDocumentVersion(r.data);
      } catch {
        // 404 is OK while assembly is mid-write
      }
    }
    setLoading(false);
    return data;
  };
  useEffect(() => {
    load().catch((e) => { setError(e.response?.data?.detail || "Could not load"); setLoading(false); });
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, designRequestId]);

  // Poll while generating
  useEffect(() => {
    if (request?.status !== "generating") {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => {
      load().catch(() => {});
    }, 2500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [request?.status]);

  const generate = async () => {
    setError("");
    try {
      await api.post(`/brands/${brandId}/design-requests/${designRequestId}/generate`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start generation");
    }
  };

  if (loading) return <p className="text-[#6B6358]">Loading…</p>;
  if (error) return <p className="text-[#B5462F]">{error}</p>;
  if (!request) return null;

  const status = STATUS_COPY[request.status] || { label: request.status, color: "#3D3A35" };

  // Group elements by panel for rendering.
  const panels = {};
  (document?.elements || []).forEach((el) => {
    panels[el.panel_id] = panels[el.panel_id] || { panel_id: el.panel_id, panel_role: el.panel_role, elements: [], warnings: [] };
    panels[el.panel_id].elements.push(el);
  });
  // Tier the warnings: document-level (no panel_id), panel-level (panel_id only), element-level (panel_id + slot_id).
  // Legacy: very old documents stored warnings as strings — normalize.
  const documentWarnings = [];
  const panelWarnings = {};
  const elementWarnings = {};
  (document?.warnings || []).forEach((raw) => {
    const w = typeof raw === "string"
      ? { panel_id: null, slot_id: null, severity: "warning", message: raw }
      : raw;
    if (!w.panel_id) {
      documentWarnings.push(w);
    } else if (w.slot_id) {
      const key = `${w.panel_id}::${w.slot_id}`;
      elementWarnings[key] = (elementWarnings[key] || []).concat(w);
      if (panels[w.panel_id]) panels[w.panel_id].warnings.push(w);
    } else {
      panelWarnings[w.panel_id] = (panelWarnings[w.panel_id] || []).concat(w);
      if (panels[w.panel_id]) panels[w.panel_id].warnings.push(w);
    }
  });
  const panelList = Object.values(panels);

  // Inputs-step rows: every element the assembly agent could not fully
  // resolve. The marketer chooses how to handle each (supply / omit /
  // pause for brand → creates a Blocker).
  const unresolvedRows = (document?.elements || [])
    .filter((el) => el.resolved === false || el.copy_source === "llm_pending")
    .map((el) => {
      const key = `${el.panel_id}::${el.slot_id}`;
      const ws = elementWarnings[key] || [];
      const whatTried = ws.map((w) => w.message).join(" ") || null;
      return {
        panel_id: el.panel_id,
        panel_role: el.panel_role,
        slot_id: el.slot_id,
        slot_label: el.slot_label || el.slot_id,
        kind: el.kind,
        subject_hint: el.subject_hint || el.binding_slug || null,
        what_the_ai_tried: whatTried,
      };
    });

  // Current step: marketer override wins; otherwise default to Inputs
  // while there are unresolved rows OR the document is paused, else
  // Content.
  const defaultStep =
    document?.lifecycle_substate === "paused_for_brand"
      ? "inputs"
      : unresolvedRows.length > 0
      ? "inputs"
      : "content";
  const currentStep = stepOverride || defaultStep;

  const onElementEdit = async (panelId, slotId, newValue) => {
    if (!document) return;
    await api.put(`/document-versions/${document.document_version_id}/elements/${panelId}/${slotId}`, {
      value: newValue,
    });
    await load();
  };

  return (
    <div data-testid={DESIGN_REQUESTS.detailRoot} className="max-w-6xl">
      <Link to={`/brands/${brandId}/design-requests`} className="text-xs uppercase tracking-[0.18em] text-[#B5462F]">
        &larr; All design requests
      </Link>
      <div className="flex items-baseline justify-between gap-4 mt-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#6B6358]">Design request</p>
          <h1 className="font-serif text-4xl tracking-tight">
            {request.name || request.promotion || request.campaign || "Untitled draft"}
          </h1>
          <p className="font-mono text-[10px] text-[#6B6358] mt-1">{request.design_request_id}</p>
        </div>
        <span
          data-testid={DESIGN_REQUESTS.detailStatus}
          className="text-xs uppercase tracking-[0.18em] px-3 py-1 rounded-full"
          style={{ backgroundColor: `${status.color}1a`, color: status.color }}
        >
          {status.label}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-end mt-6">
        <div data-testid={DESIGN_REQUESTS.detailBrief} className="border border-[#1A1F2C]/10 bg-white rounded-lg p-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">Additional notes</p>
          <p className="text-sm text-[#3D3A35] whitespace-pre-wrap">
            {request.campaign_brief || <em className="text-[#6B6358]">No notes.</em>}
          </p>
        </div>
        <button
          data-testid={DESIGN_REQUESTS.generateButton}
          onClick={generate}
          disabled={request.status === "generating"}
          className="px-5 py-3 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-60 self-start whitespace-nowrap"
        >
          {request.status === "generating" ? "Generating…" : request.document_version_id ? "Re-generate" : "Generate wrap"}
        </button>
      </div>

      {user?.is_platform_admin && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            data-testid={DESIGN_REQUESTS.previewPromptButton}
            onClick={() => setShowPromptModal(true)}
            className="text-xs uppercase tracking-[0.18em] text-[#B5462F] hover:text-[#1A1F2C] underline underline-offset-4"
          >
            Preview prompt →
          </button>
        </div>
      )}

      {request.failure_reason && (
        <div className="mt-5 border border-[#B5462F]/30 bg-[#B5462F]/5 rounded-lg p-4 text-sm text-[#B5462F]">
          <strong>Generation failed:</strong> {request.failure_reason}
        </div>
      )}

      {document && (
        <WorkflowStepper
          current={currentStep}
          furthest={furthestStep}
          onJump={setStepOverride}
        />
      )}

      {currentStep === "inputs" && document && (
        <InputsStep
          document={document}
          unresolvedRows={unresolvedRows}
          onChanged={() => load()}
          onAdvance={() => setStepOverride("content")}
        />
      )}

      {(currentStep === "content" || currentStep === "design") && request.assembly_summary && (
        <div data-testid={DESIGN_REQUESTS.detailSummary} className="mt-5 grid grid-cols-3 gap-3">
          <Stat label="Elements" value={request.assembly_summary.elements} />
          <Stat label="Warnings" value={request.assembly_summary.warnings} highlight={request.assembly_summary.warnings > 0} />
          <Stat label="Missing assets" value={request.assembly_summary.missing_assets} highlight={request.assembly_summary.missing_assets > 0} />
        </div>
      )}

      {currentStep === "design" && document && (
        <ApprovalBanner
          document={document}
          errorCount={documentWarnings.filter((w) => w.severity === "error").length
            + Object.values(elementWarnings).flat().filter((w) => w.severity === "error").length}
          onChange={() => load()}
        />
      )}

      {currentStep === "content" && documentWarnings.length > 0 && (
        <details data-testid={DESIGN_REQUESTS.detailWarnings} className="mt-5 border border-[#1A1F2C]/10 bg-white rounded-lg p-4" open>
          <summary className="text-sm cursor-pointer text-[#3D3A35]">
            {documentWarnings.length} document-level warning{documentWarnings.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-3 space-y-1.5 text-xs text-[#3D3A35]">
            {documentWarnings.map((w, i) => (
              <li key={i} className="border-l-2 border-[#B5462F]/40 pl-3">
                <span className="uppercase tracking-[0.15em] text-[10px] mr-2 text-[#B5462F]">{w.severity}</span>
                {w.message}
              </li>
            ))}
          </ul>
        </details>
      )}

      {(currentStep === "content" || currentStep === "design") && panelList.length > 0 && (
        <div className="mt-8">
          <h2 className="font-serif text-2xl">
            {currentStep === "content" ? "Content review" : "Design review"}
          </h2>
          <p className="text-sm text-[#3D3A35] mt-2 mb-4 max-w-2xl">
            {currentStep === "content"
              ? "How the AI resolved each section. The actual visual layout is produced by Pica9 PDFMaker after you generate the design."
              : "The rendered layout. Click into any section to edit content; regenerate to refresh."}
          </p>
          {currentStep === "content" && (
            <div
              data-testid="content-step-trafficlight-legend"
              className="border border-[#1A1F2C]/10 bg-white rounded-lg px-4 py-3 mb-4 text-xs text-[#3D3A35] max-w-3xl"
            >
              <p className="font-medium text-[#1A1F2C] mb-1.5">A note on editing</p>
              <p className="leading-relaxed">
                The AI started from your brand's Content Library and edited each
                slot to fit this document type. You can change anything — but
                the input box will signal how far you've moved from what fits:
              </p>
              <div className="flex flex-wrap gap-3 mt-2 text-[11px]">
                <span className="px-2 py-0.5 rounded font-medium tracking-[0.1em]"
                      style={{ backgroundColor: "#1F6B6B", color: "#F8F5EE" }}>ON-SPEC</span>
                <span className="text-[#3D3A35]">≤ 10% drift — should render cleanly.</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-[11px]">
                <span className="px-2 py-0.5 rounded font-medium tracking-[0.1em]"
                      style={{ backgroundColor: "#C28A2E", color: "#F8F5EE" }}>DRIFT</span>
                <span className="text-[#3D3A35]">10–25% — fit may tighten or loosen.</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-[11px]">
                <span className="px-2 py-0.5 rounded font-medium tracking-[0.1em]"
                      style={{ backgroundColor: "#B5462F", color: "#F8F5EE" }}>OVERFLOW</span>
                <span className="text-[#3D3A35]">&gt; 25% — layout may break. Use "Restore AI version" to revert.</span>
              </div>
            </div>
          )}
          {currentStep === "content" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {panelList.map((p) => (
                <PanelCard key={p.panel_id} panel={p} elementWarnings={elementWarnings} onElementEdit={onElementEdit} />
              ))}
            </div>
          )}
          {currentStep === "design" && document && (
            <DesignStep
              document={document}
              panelList={panelList}
              onChanged={() => load()}
            />
          )}
          {currentStep === "content" && (
            <div className="mt-6">
              <button
                data-testid="content-step-advance"
                onClick={() => setStepOverride("design")}
                className="px-5 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium"
              >
                Continue to Design →
              </button>
            </div>
          )}
        </div>
      )}

      {showPromptModal && (
        <PreviewPromptModal
          brandId={brandId}
          designRequestId={designRequestId}
          onClose={() => setShowPromptModal(false)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`border rounded-lg px-4 py-3 ${highlight ? "border-[#B5462F]/40 bg-[#B5462F]/5" : "border-[#1A1F2C]/10 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358]">{label}</div>
      <div className={`text-2xl font-serif tracking-tight mt-1 ${highlight ? "text-[#B5462F]" : "text-[#1A1F2C]"}`}>
        {value}
      </div>
    </div>
  );
}

function PanelCard({ panel, elementWarnings, onElementEdit }) {
  // Find the background fill (first color_fill element).
  const bgElem = panel.elements.find((e) => e.kind === "color_fill");
  const bgHex = bgElem?.asset_summary?.hex || "#F8F5EE";

  // Find primary text color.
  const textColorElem = panel.elements.find((e) => e.kind === "color_text");
  const textHex = textColorElem?.asset_summary?.hex || "#1A1F2C";

  // Find primary headline font.
  const fontElem = panel.elements.find((e) => e.kind === "font");
  const fontFamily = fontElem?.asset_summary?.family || "serif";

  // Find a logo to put at top.
  const logoElem = panel.elements.find((e) => e.kind === "logo");

  // Find the headline-like text element (heuristic).
  const headline = panel.elements.find((e) => e.kind === "text" && /headline|copy/i.test(e.slot_id));
  const otherTexts = panel.elements.filter((e) => e.kind === "text" && e !== headline);

  const panelHasError   = panel.warnings.some((w) => w.severity === "error");
  const panelHasWarning = panel.warnings.some((w) => w.severity === "warning");
  const tierColor = panelHasError ? "#B5462F" : panelHasWarning ? "#C28A2E" : "#1F6B6B";

  return (
    <div
      data-testid={`${DESIGN_REQUESTS.detailPanelPrefix}${panel.panel_id}`}
      className="rounded-lg overflow-hidden border"
      style={{ borderColor: panel.warnings.length ? `${tierColor}66` : "rgba(26,31,44,0.10)" }}
    >
      <div
        className="aspect-[3/4] p-5 flex flex-col justify-between"
        style={{ backgroundColor: bgHex, color: textHex, fontFamily }}
      >
        <div>
          {logoElem?.asset_summary?.image_url ? (
            <img src={logoElem.asset_summary.image_url} alt="logo" className="max-w-[60%] max-h-12 object-contain" />
          ) : logoElem ? (
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-60">
              [{logoElem.binding_slug || "logo"} missing]
            </div>
          ) : null}
          {headline && (
            <h3 className="text-lg font-bold leading-tight mt-3">{headline.value}</h3>
          )}
        </div>
        <div className="space-y-1.5 text-xs leading-relaxed mt-4 opacity-90">
          {otherTexts.slice(0, 4).map((t) => (
            <p key={t.slot_id} className="line-clamp-3">{t.value}</p>
          ))}
        </div>
      </div>
      <div className="bg-white px-4 py-3 border-t border-[#1A1F2C]/10">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#B5462F]">
              {panel.panel_role || "panel"}
            </div>
            <div className="font-mono text-xs text-[#6B6358] mt-0.5">{panel.panel_id}</div>
          </div>
          {panel.warnings.length > 0 && (
            <span
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ backgroundColor: `${tierColor}1a`, color: tierColor }}
            >
              {panelHasError ? `${panel.warnings.length} blocker` : `${panel.warnings.length} warn`}
            </span>
          )}
        </div>
        <details className="mt-2">
          <summary className="text-[11px] cursor-pointer text-[#3D3A35]">
            {panel.elements.length} slots
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] text-[#3D3A35]">
            {panel.elements.map((el) => {
              const key = `${panel.panel_id}::${el.slot_id}`;
              const ws = elementWarnings[key] || [];
              const isError   = ws.some((w) => w.severity === "error");
              const isWarning = ws.some((w) => w.severity === "warning");
              const dot = isError ? "bg-[#B5462F]" : isWarning ? "bg-[#C28A2E]" : (el.resolved ? "bg-[#1F6B6B]" : "bg-[#6B6358]");
              return (
                <li
                  key={el.slot_id}
                  data-testid={`${DESIGN_REQUESTS.detailElementPrefix}${panel.panel_id}-${el.slot_id}`}
                  className="space-y-0.5"
                >
                  <div className="flex gap-2 items-baseline">
                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full mt-1 ${dot}`} />
                    <span className="font-mono text-[10px] text-[#6B6358]">{el.slot_id}</span>
                    {el.kind === "text" ? (
                      <InlineTextEdit
                        value={el.value || ""}
                        baselineLength={el.baseline_length}
                        baselineValue={el.baseline_value}
                        onSave={(v) => onElementEdit(panel.panel_id, el.slot_id, v)}
                      />
                    ) : (
                      <span className="truncate">{el.binding_slug ? `→ ${el.binding_slug}` : (el.value || "—")}</span>
                    )}
                  </div>
                  {ws.length > 0 && (
                    <ul className="pl-5 space-y-0.5">
                      {ws.map((w, i) => (
                        <li key={i} className="text-[10px] text-[#B5462F]" data-severity={w.severity}>
                          {w.message}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      </div>
    </div>
  );
}

function InlineTextEdit({ value, baselineLength, baselineValue, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [lastSeen, setLastSeen] = useState(value);
  if (value !== lastSeen) {
    setLastSeen(value);
    setDraft(value);
  }

  // Traffic light: how far has the marketer pulled this slot away from
  // the AI's space-aware draft?
  //   ≤ 10%  → green  (on-spec)
  //   ≤ 25%  → yellow (watch the fit)
  //   > 25%  → red    (may break layout)
  // The signal lives on the displayed text AND on the input while
  // editing so a marketer can see the cost of every keystroke. No hard
  // block — they can always proceed.
  const computeTier = (current) => {
    const ref = baselineLength || (baselineValue ? baselineValue.length : 0);
    if (!ref) return null;
    const len = (current || "").length;
    const delta = Math.abs(len - ref) / ref;
    if (delta <= 0.10) return { tier: "green", delta, message: "On-spec — AI sized this for the slot." };
    if (delta <= 0.25) return { tier: "yellow", delta, message: "Watch the fit — content has grown / shrunk past 10%." };
    return { tier: "red", delta, message: "May break layout — content drift exceeds 25%." };
  };
  const tierStyles = {
    green:  { bg: "#1F6B6B1a", border: "#1F6B6B66", chip: "#1F6B6B", label: "ON-SPEC" },
    yellow: { bg: "#C28A2E1a", border: "#C28A2E66", chip: "#C28A2E", label: "DRIFT" },
    red:    { bg: "#B5462F1a", border: "#B5462F66", chip: "#B5462F", label: "OVERFLOW" },
  };
  const showLight = !!baselineLength || (baselineValue && baselineValue.length > 0);
  const editedFromBaseline = showLight && baselineValue != null && value !== baselineValue;

  if (!editing) {
    const tierData = computeTier(value);
    const style = tierData ? tierStyles[tierData.tier] : null;
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`inline-edit-${tierData?.tier || "neutral"}`}
        className="truncate text-left hover:underline cursor-text flex-1 min-w-0"
        title={tierData ? `${tierData.message} Click to edit.` : "Click to edit"}
        style={style && editedFromBaseline ? {
          backgroundColor: style.bg,
          borderLeft: `2px solid ${style.border}`,
          paddingLeft: "6px",
        } : undefined}
      >
        {value || "—"}
      </button>
    );
  }

  const tierData = computeTier(draft);
  const style = tierData ? tierStyles[tierData.tier] : null;
  return (
    <div className="flex-1 flex flex-col gap-1">
      <div className="flex gap-1 items-center">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setEditing(false); setDraft(value); }
            if (e.key === "Enter") { onSave(draft); setEditing(false); }
          }}
          data-testid={`inline-edit-input-${tierData?.tier || "neutral"}`}
          className="flex-1 px-1.5 py-0.5 text-[11px] border rounded transition-colors"
          style={style ? {
            backgroundColor: style.bg,
            borderColor: style.border,
          } : {
            backgroundColor: "#F8F5EE",
            borderColor: "rgba(26,31,44,0.30)",
          }}
        />
        <button
          type="button"
          onClick={() => { onSave(draft); setEditing(false); }}
          className="text-[10px] px-1.5 py-0.5 bg-[#1A1F2C] text-[#F8F5EE] rounded"
        >save</button>
        <button
          type="button"
          onClick={() => { setEditing(false); setDraft(value); }}
          className="text-[10px] text-[#6B6358]"
        >x</button>
      </div>
      {style && (
        <div className="flex gap-2 items-center text-[10px]">
          <span
            className="px-1.5 py-0.5 rounded font-medium tracking-[0.1em]"
            style={{ backgroundColor: style.chip, color: "#F8F5EE" }}
          >
            {style.label}
          </span>
          <span className="text-[#3D3A35]">
            {Math.round(tierData.delta * 100)}% drift · {style && tierData.message}
          </span>
          {baselineValue && draft !== baselineValue && (
            <button
              type="button"
              onClick={() => setDraft(baselineValue)}
              className="ml-auto underline text-[#3D3A35]"
              data-testid="inline-edit-restore-ai"
            >
              Restore AI version
            </button>
          )}
        </div>
      )}
    </div>
  );
}
